import {
  type WebhookConditionDraft,
  type WebhookConditionOperator,
  type WebhookPayloadField,
} from '@littlebird/sdk';
import { useId } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Input, XStack } from '@/components/ui';
import { Button } from '@/components/ui/Button';
import { DropdownMenu } from '@/components/ui/dropdown-menu';

type OperatorOption = {
  value: string;
  label: string;
  operator: WebhookConditionOperator;
  negate: boolean;
};

const OPERATOR_OPTIONS: OperatorOption[] = [
  { value: 'equals', label: 'Equals', operator: 'equals', negate: false },
  {
    value: 'not-equals',
    label: 'Does not equal',
    operator: 'equals',
    negate: true,
  },
  { value: 'contains', label: 'Contains', operator: 'contains', negate: false },
  {
    value: 'not-contains',
    label: 'Does not contain',
    operator: 'contains',
    negate: true,
  },
  { value: 'exists', label: 'Exists', operator: 'exists', negate: false },
  {
    value: 'not-exists',
    label: 'Does not exist',
    operator: 'exists',
    negate: true,
  },
  {
    value: 'regex',
    label: 'Matches pattern',
    operator: 'regex',
    negate: false,
  },
  {
    value: 'not-regex',
    label: 'Does not match pattern',
    operator: 'regex',
    negate: true,
  },
];

const optionFor = (operator: WebhookConditionOperator, negate: boolean) =>
  OPERATOR_OPTIONS.find(
    (o) => o.operator === operator && o.negate === negate
  ) ?? OPERATOR_OPTIONS[0];

export function WebhookConditionRow({
  condition,
  fields,
  onChange,
  onRemove,
}: {
  condition: WebhookConditionDraft;
  /** Paths from the endpoint's sample payload, offered as suggestions; any other path is still allowed. */
  fields: WebhookPayloadField[];
  onChange: (next: WebhookConditionDraft) => void;
  onRemove: () => void;
}) {
  const selected = optionFor(condition.operator, condition.negate);
  const showValue = condition.operator !== 'exists';
  const fieldListId = useId();

  return (
    <XStack className="flex-wrap items-center gap-2">
      <Input
        aria-label="Payload field path"
        placeholder={
          fields.length > 0 ? `e.g. ${fields[0].path}` : 'e.g. event.type'
        }
        list={fields.length > 0 ? fieldListId : undefined}
        value={condition.path}
        onChange={(event) =>
          onChange({ ...condition, path: event.target.value })
        }
        className="h-8 min-w-32 flex-1"
      />
      {fields.length > 0 && (
        <datalist id={fieldListId}>
          {fields.map((field) => (
            <option key={field.path} value={field.path}>
              {field.type}
            </option>
          ))}
        </datalist>
      )}
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <Button
            aria-label={`Condition: ${selected.label}`}
            variant="outline"
            size="sm"
            className="gap-1.5 px-2"
          >
            <span className="truncate">{selected.label}</span>
            <Icons.chevronDown className="size-3.5 shrink-0" />
          </Button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content align="start">
          <DropdownMenu.RadioGroup
            value={selected.value}
            onValueChange={(value) => {
              const option = OPERATOR_OPTIONS.find((o) => o.value === value);
              if (!option) return;
              onChange({
                ...condition,
                operator: option.operator,
                negate: option.negate,
                value: option.operator === 'exists' ? null : condition.value,
              });
            }}
          >
            {OPERATOR_OPTIONS.map((option) => (
              <DropdownMenu.RadioItem key={option.value} value={option.value}>
                {option.label}
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
        </DropdownMenu.Content>
      </DropdownMenu.Root>
      {showValue && (
        <Input
          aria-label="Condition value"
          placeholder="value"
          value={condition.value ?? ''}
          onChange={(event) =>
            onChange({ ...condition, value: event.target.value })
          }
          className="h-8 min-w-24 flex-1"
        />
      )}
      <Button
        aria-label="Remove condition"
        variant="ghost"
        size="icon-sm"
        iconOnly
        className="shrink-0 text-foreground-muted"
        onClick={onRemove}
      >
        <Icons.trash2 />
      </Button>
    </XStack>
  );
}
